import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { identifyUser, resetAnalytics } from '../lib/analytics';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('verifaeye_token'));
  const [loading, setLoading] = useState(true);
  const [usage, setUsage] = useState(null);

  const logout = useCallback(({ redirectTo = null } = {}) => {
    localStorage.removeItem('verifaeye_token');
    setToken(null);
    setUser(null);
    setUsage(null);
    resetAnalytics();
    // A full load clears every cached screen and avoids ProtectedRoute racing us to /login
    if (redirectTo) window.location.assign(redirectTo);
  }, []);

  const fetchUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      setUsage(response.data.usage);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  }, [token, logout]);

  useEffect(() => {
    // CRITICAL: If returning from Google OAuth, skip the /me check here.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token, fetchUser]);

  useEffect(() => {
    if (user?.id) identifyUser(user);
  }, [user]);

  const refreshUsage = useCallback(async () => {
    if (!token) return null;
    try {
      const response = await axios.get(`${API_URL}/api/usage/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsage(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch usage:', error);
      return null;
    }
  }, [token]);

  const login = useCallback(async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, {
      email,
      password
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('verifaeye_token', access_token);
    setToken(access_token);
    setUser(userData);
    setUsage(userData.usage);
    return userData;
  }, []);

  const register = useCallback(async (name, email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/register`, {
      name,
      email,
      password
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('verifaeye_token', access_token);
    setToken(access_token);
    setUser(userData);
    setUsage(userData.usage);
    return userData;
  }, []);

  const getAuthHeader = useCallback(() => ({
    Authorization: `Bearer ${token}`
  }), [token]);

  const loginWithToken = useCallback((access_token, userData) => {
    localStorage.setItem('verifaeye_token', access_token);
    setToken(access_token);
    setUser(userData);
    setUsage(userData.usage);
    return userData;
  }, []);

  const value = useMemo(() => ({
    user,
    token,
    loading,
    usage,
    login,
    register,
    loginWithToken,
    logout,
    getAuthHeader,
    refreshUsage,
    isAuthenticated: !!user,
    emailVerified: user?.email_verified || false
  }), [user, token, loading, usage, login, register, loginWithToken, logout, getAuthHeader, refreshUsage]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
